import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import ArticleIcon from "@mui/icons-material/Article";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import { useGlobalContext, useGlobalContextUpdate } from "@contexts/Global";
import { CORPORATION } from "@/constants";
import theme from "@/theme";

const Check = () => (
  <CheckCircleIcon
    sx={{
      color: "green",
      position: "absolute",
      left: "5px",
      top: "5px",
      fontSize: "28px",
    }}
  />
);

const Table = (props = {}) => {
  const { corporation = "", data = {} } = props;
  const { mesa } = data;
  const options = ["e14", "claim", "register", "votes"];
  return (
    <Box
      onClick={props.onClick}
      sx={{
        height: "179px",
        minWidth: "179px",
        borderRadius: "8px",
        transition: "all 0.25s ease-in",
        cursor: "pointer",
        backgroundColor: "whitesmoke",
        position: "relative",
        opacity: 0.8,
        "&:hover": {
          opacity: 1,
          transform: "scale(1.05)",
        },
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        spacing={1}
      >
        <Grid item>
          <Typography
            sx={{
              fontSize: "80px",
              height: "100%",
              width: "20px",
              position: "relative",
              top: "10px",
              left: "-5px",
              color: theme.palette.primary.main,
            }}
            variant="caption"
            display="block"
            gutterBottom
          >
            {mesa}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            position: "relative",
            width: "31px",
            height: "100%",
            marginTop: "12px",
            left: "8px",
            top: "0px",
          }}
        >
          {options.map((option, index) =>
            getIconByOption({ id: index, option, table: data, corporation })
          )}
        </Grid>
      </Grid>
      {isTableComplete({ table: data }) && <Check />}
    </Box>
  );
};

const getColorByIcon = (name = "") => {
  const {
    main = "",
    secondary = "",
    third = "",
    fourth = "",
  } = theme.palette.dashbaord;
  if (name === "e14") {
    return main;
  }
  if (name === "votes") {
    return fourth;
  }
  if (name === "claim") {
    return third;
  }
  if (name === "register") {
    return secondary;
  }
};

const isTableComplete = ({ table = {} }) => {
  let hasCompleteAllItems = true;

  if (!table.E14_sen) {
    hasCompleteAllItems = false;
  }
  if (!table.cuenta_votos_sen) {
    hasCompleteAllItems = false;
  }
  if (!table.ingreso_datos_sen) {
    hasCompleteAllItems = false;
  }

  if (!table.E14_cam) {
    hasCompleteAllItems = false;
  }
  if (!table.cuenta_votos_cam) {
    hasCompleteAllItems = false;
  }
  if (!table.ingreso_datos_cam) {
    hasCompleteAllItems = false;
  }

  if (!table.E14_consulta) {
    hasCompleteAllItems = false;
  }
  if (!table.cuenta_votos_consulta) {
    hasCompleteAllItems = false;
  }
  if (!table.ingreso_datos_consulta) {
    hasCompleteAllItems = false;
  }

  return hasCompleteAllItems;
};

const getIconByOption = ({
  option = "",
  corporation = "",
  id = "",
  table = {},
}) => {
  const isSenado = CORPORATION.SENADO === corporation;
  const isCamara = CORPORATION.CAMARA === corporation;
  const isConsulta = CORPORATION.CONSULTA === corporation;
  const isE14 = option === "e14";
  const isVotes = option === "votes";
  const isClaim = option === "claim";
  const isRegister = option === "register";
  const styles = {
    fontSize: "30px",
  };
  if (isSenado) {
    if (table.E14_sen && isE14) {
      styles.color = getColorByIcon("e14");
      return <CameraAltIcon key={id} sx={styles} />;
    }
    if (table.cuenta_votos_sen && isVotes) {
      styles.color = getColorByIcon("votes");
      return <HowToVoteIcon key={id} sx={styles} />;
    }
    if (table.reclamaciones_sen && isClaim) {
      styles.color = getColorByIcon("claim");
      return <PlagiarismIcon key={id} sx={styles} />;
    }
    if (table.ingreso_datos_sen && isRegister) {
      styles.color = getColorByIcon("register");
      return <ArticleIcon key={id} sx={styles} />;
    }
  }

  if (isCamara) {
    if (table.E14_cam && isE14) {
      styles.color = getColorByIcon("e14");
      return <CameraAltIcon key={id} sx={styles} />;
    }
    if (table.cuenta_votos_cam && isVotes) {
      styles.color = getColorByIcon("votes");
      return <HowToVoteIcon key={id} sx={styles} />;
    }
    if (table.reclamaciones_cam && isClaim) {
      styles.color = getColorByIcon("claim");
      return <PlagiarismIcon key={id} sx={styles} />;
    }
    if (table.ingreso_datos_cam && isRegister) {
      styles.color = getColorByIcon("register");
      return <ArticleIcon key={id} sx={styles} />;
    }
  }

  if (isConsulta) {
    if (table.E14_consulta && isE14) {
      styles.color = getColorByIcon("e14");
      return <CameraAltIcon key={id} sx={styles} />;
    }
    if (table.cuenta_votos_consulta && isVotes) {
      styles.color = getColorByIcon("votes");
      return <HowToVoteIcon key={id} sx={styles} />;
    }
    if (table.reclamaciones_consulta && isClaim) {
      styles.color = getColorByIcon("claim");
      return <PlagiarismIcon key={id} sx={styles} />;
    }
    if (table.ingreso_datos_consulta && isRegister) {
      styles.color = getColorByIcon("register");
      return <ArticleIcon key={id} sx={styles} />;
    }
  }
  return null;
};

export const SwitchTable = () => {
  const { data = {}, corporation = "" } = useGlobalContext();
  const setGlobalContext = useGlobalContextUpdate();
  const { tables = [] } = data;

  const handleSwitchTable = (index) => {
    const currentTable = tables[index];
    setGlobalContext({
      modalContent: "",
      isModalOpen: false,
      data: {
        ...data,
        table: currentTable.mesa,
        currentTable,
      },
    });
  };

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-center"
      alignItems="center"
      spacing={2}
      sx={{
        justifyContent: "center",
        marginTop: "40px",
      }}
    >
      {tables.map((currentTable, index) => (
        <Grid item key={index}>
          <Table
            onClick={() => handleSwitchTable(index)}
            data={currentTable}
            corporation={corporation}
          />
        </Grid>
      ))}
    </Grid>
  );
};
